import { createConsumer, Consumer, Subscription } from "@rails/actioncable";
import * as Y from "yjs";
import { WebsocketProvider } from "@y-rb/actioncable";
import { AwarenessUser } from "../AwarenessUsers/types";
import { syncMicrositeYDocs } from "@getregistered/greg-editors";
import { logMicrositeUpdate } from "./utils";
import { AwarenessControl } from "../AwarenessUsers/AwarenessControl";
import { stringToHslColor } from "../AwarenessUsers/colors";
import {getConsumer} from "../actionCableConsumerPool";

// Base64 helper used to ship the ydoc state as a JSON-safe string in the
// save RPC payload. Matches the encoding used by @y-rb/actioncable for
// on-wire Yjs updates so the server-side `y` gem can decode directly.
function encodeBinaryToBase64(bin: Uint8Array): string {
  const chars = Array.from(bin, (ch) => String.fromCharCode(ch)).join("");
  return btoa(chars);
}

// Listener subscribed to save errors. Receives the server's error message
// (often an ActiveRecord::RecordInvalid string already user-readable) so the
// wrapping React component can render it as a Flash banner instead of letting
// it disappear into devtools.
export type SaveErrorListener = (message: string) => void;

// Connection lifecycle, mirrored from FormChannelControl. "connecting" until
// the provider reports its first sync; "ready" while synced; "disconnected"
// once sync has been lost for longer than the debounce window.
export type ReadyState = "connecting" | "ready" | "disconnected";

export type ReadyStateListener = (state: ReadyState) => void;

export class MicrositeChannelControl {
  ydoc: Y.Doc;
  id: number;
  initialAwarenessUser: AwarenessUser;
  awarenessControl: AwarenessControl;
  consumer: Consumer;
  provider: WebsocketProvider;
  persistenceChannelSubscription: Subscription;
  baseDocument: Y.Doc | null;
  private saveErrorListeners: Set<SaveErrorListener> = new Set();
  // GR-1921 connection-health state, ported from FormChannelControl.
  private _readyState: ReadyState = "connecting";
  private readyStateListeners: Set<ReadyStateListener> = new Set();
  private syncCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private syncedObserverCleanup: (() => void) | null = null;
  private browserConnectivityCleanup: (() => void) | null = null;

  constructor(
    id: number,
    awarenessUser: AwarenessUser,
    cableUrl?: string
  ) {
    this.id = id;
    this.initialAwarenessUser = awarenessUser;

    // GR-2123/GR-2203: garbage collection ON so tombstones are collected and
    // the server Y.Doc doesn't bloat (originally org-gated, now global).
    this.ydoc = new Y.Doc({ gc: true });
    this.ydoc.on("update", () => {
      logMicrositeUpdate(this.ydoc);
    });
    this.baseDocument = null;

    this.consumer = getConsumer(cableUrl);
    this.provider = new WebsocketProvider(
      this.ydoc,
      this.consumer,
      "MicrositeChannel",
      {
        id: id.toString(),
      }
    );
    // The @y-rb/actioncable provider calls channel.send() fire-and-forget (the
    // returned Promise is never awaited). When the channel is closed (e.g.
    // after a SubscriptionTimeoutError), Channel.perform() rejects with
    // "Channel is not subscribed" as an unhandled promise rejection. Patch the
    // subscription's send() to swallow these during disconnection.
    const providerSub = this.provider.channel as unknown as {
      channel?: { send?: (data: unknown) => Promise<unknown> };
      send?: (data: unknown) => void;
    };
    if (providerSub) {
      const innerChannel = providerSub.channel;
      providerSub.send = function (data: unknown) {
        innerChannel?.send?.(data)?.catch?.(() => {});
      };
    }

    this.awarenessControl = new AwarenessControl(
      this.provider,
      this.initialAwarenessUser
    );
    this.persistenceChannelSubscription = this.consumer.subscriptions.create(
      { channel: "MicrositePersistenceChannel", id: id },
      {
        received: this.receivePersistenceMessage.bind(this),
      }
    );

    // GR-1921: detect first sync, then ongoing connect/disconnect, and actively
    // revive the cable on network/visibility changes. The microsite editor
    // previously had none of this — a dropped cable (notably token-expired →
    // 'closed', which the Monitor will NOT auto-recover) left the in-memory
    // YDoc silently diverged from the server with no resync and no warning,
    // which is the GR-1921 "page changes not syncing to preview" failure.
    this.monitorConnection();
    this.observeBrowserConnectivity();
  }

  receivePersistenceMessage(data: number[] | { type: string; message?: string }): void {
    if (!Array.isArray(data)) {
      if (data.type === "save_error") {
        this.handleSaveError(data.message ?? "");
        return;
      }
      if (data.type === "force_reload") {
        // Dev seeder wiped Redis and is telling us our in-memory YDoc is
        // stale. A full reload is the simplest way to re-fetch the fresh
        // server state without risking a stale client-side flush.
        window.location.reload();
        return;
      }
    }
    this.receiveBaseDocument(data as number[]);
  }

  receiveBaseDocument(data: number[]): void {
    console.log("Received persistence update", data);
    const baseDocument = new Y.Doc({ gc: true });
    baseDocument.transact(() => {
      Y.applyUpdate(baseDocument, new Uint8Array(data));
    });
    this.baseDocument = baseDocument;
  }

  private handleSaveError(message: string): void {
    console.error("[MicrositeEditor] Save failed:", message);
    // Restore isDirty=true so the user knows their changes weren't persisted.
    // This must be set from the client (not the server) because the client's
    // CRDT clock is higher, ensuring LWW resolution keeps this value.
    this.ydoc.getMap("editorState").set("isDirty", true);
    // Surface the error to subscribed UI listeners so the wrapping component
    // can render a Flash banner. Without this the error would only exist in
    // devtools and the user would see a confusing "Saved → Unsaved" flicker
    // with no explanation. Listeners are notified out-of-band so a throwing
    // listener can't block other listeners or the dirty-state flip above.
    this.saveErrorListeners.forEach((listener) => {
      try {
        listener(message);
      } catch (err) {
        console.error("[MicrositeEditor] save error listener threw:", err);
      }
    });
  }

  /**
   * Subscribe to save errors broadcast by MicrositeChannel#save's rescue.
   * Returns an unsubscribe function. Use this in a useEffect from the
   * wrapping React component to render an error banner.
   */
  onSaveError(listener: SaveErrorListener): () => void {
    this.saveErrorListeners.add(listener);
    return () => {
      this.saveErrorListeners.delete(listener);
    };
  }

  save(): void {
    // GR-1921: refuse to save while the cable is down. Without this guard the
    // perform() below is swallowed by the consumer pool's performFailures:"warn"
    // setting, so the user sees the "Unsaved" indicator clear with no error and
    // believes their work is saved when nothing left the browser. The in-memory
    // YDoc still holds every edit and will resync once the cable reconnects.
    if (this._readyState === "disconnected") {
      this.handleSaveError(
        "You're disconnected — your changes are safe in this tab and will sync once the connection is restored. Don't reload the page."
      );
      return;
    }

    // Snapshot the client's full ydoc state and ship it with the save RPC.
    // The server merges this on top of whatever it finds in Redis before
    // writing to Postgres, which closes the broadcast → save race: if any
    // of our recent Yjs updates hadn't reached Redis via the WebsocketProvider
    // before this RPC arrives, the merge guarantees they're still included.
    //
    // Yjs CRDT ops are commutative, so applying our state to an already
    // up-to-date Redis is a safe no-op. Multi-user still works because
    // Redis remains the shared source of truth — we're just adding whatever
    // this client knows on top.
    const update = Y.encodeStateAsUpdate(this.ydoc);
    const state = encodeBinaryToBase64(update);
    // Catch to prevent an unhandled rejection if the channel closed between our
    // _readyState check and this send. On a cable drop the channel state flips
    // to "disconnected" immediately, but _readyState only follows after the 6s
    // debounce — a Save in that window slips past the guard above and
    // Channel.perform throws "Channel is not subscribed" *before* reaching the
    // cable, so performFailures:"warn" never sees it. Route the failure through
    // handleSaveError so the dirty flag is restored too (see below).
    Promise.resolve(this.provider.channel.perform("save", { state })).catch(
      (err) => {
        console.warn(
          "[MicrositeEditor] save perform failed (channel likely closed):",
          err?.message
        );
        this.handleSaveError(
          "You're disconnected — your changes are safe in this tab and will sync once the connection is restored. Don't reload the page."
        );
      }
    );
    this.ydoc.getMap("editorState").set("isDirty", false);
    // baseDocument is updated when the server confirms via receiveBaseDocument(),
    // not optimistically here. This ensures reset() always reverts to the last
    // known-good server state, even if this save fails.
  }

  reset(): void {
    if (this.baseDocument === null) {
      console.error("Base document is null");
      return;
    }
    syncMicrositeYDocs(this.baseDocument, this.ydoc);
    this.ydoc.getMap("editorState").set("isDirty", false);
  }

  get readyState(): ReadyState {
    return this._readyState;
  }

  /**
   * Subscribe to connection state changes. Fires immediately with the current
   * state. Returns an unsubscribe function — use it from a React useEffect to
   * render a "disconnected" banner. Mirrors the onSaveError() pattern.
   */
  onReadyStateChange(listener: ReadyStateListener): () => void {
    this.readyStateListeners.add(listener);
    listener(this._readyState);
    return () => {
      this.readyStateListeners.delete(listener);
    };
  }

  private setReadyState(state: ReadyState): void {
    if (this._readyState === state) return;
    this._readyState = state;
    console.debug("[MicrositeEditor] state transition:", state);
    this.readyStateListeners.forEach((listener) => {
      try {
        listener(state);
      } catch (err) {
        console.error("[MicrositeEditor] ready-state listener threw:", err);
      }
    });
  }

  // Poll for the provider's first sync, then hand off to the reactive
  // observer. The @y-rb/actioncable provider exposes `synced` but emits no
  // events, so the initial transition has to be polled.
  private monitorConnection(): void {
    const FAST_INTERVAL = 50; // ms — only runs until the first sync lands
    const checkInitialSync = () => {
      if (this.provider.synced) {
        this.setReadyState("ready");
        this.observeSyncedProperty();
        return;
      }
      this.syncCheckTimer = setTimeout(checkInitialSync, FAST_INTERVAL);
    };
    checkInitialSync();
  }

  // Intercept the provider's `synced` setter for reactive disconnect/reconnect
  // detection. The provider doesn't emit events, but its setter fires when the
  // ActionCable subscription's connected/disconnected callbacks flip sync
  // state. A debounce filters brief blips so we don't flap the banner.
  private observeSyncedProperty(): void {
    const DEBOUNCE_MS = 6000;
    let currentSynced = true; // we only get here once synced is true
    let disconnectTimer: ReturnType<typeof setTimeout> | null = null;

    Object.defineProperty(this.provider, "synced", {
      get: () => currentSynced,
      set: (state: boolean) => {
        if (currentSynced === state) return;
        currentSynced = state;
        if (state) {
          if (disconnectTimer) {
            clearTimeout(disconnectTimer);
            disconnectTimer = null;
          }
          if (this._readyState === "disconnected") {
            console.debug("[MicrositeEditor] connection restored");
            this.setReadyState("ready");
          }
        } else if (!disconnectTimer) {
          disconnectTimer = setTimeout(() => {
            disconnectTimer = null;
            if (!currentSynced && this._readyState !== "disconnected") {
              console.warn("[MicrositeEditor] connection lost");
              this.setReadyState("disconnected");
            }
          }, DEBOUNCE_MS);
        }
      },
      configurable: true,
    });

    this.syncedObserverCleanup = () => {
      if (disconnectTimer) clearTimeout(disconnectTimer);
      // Restore the prototype getter/setter.
      delete (this.provider as unknown as { synced?: boolean }).synced;
    };
  }

  // Use browser online/visibilitychange events to trigger an immediate
  // reconnect instead of waiting for the next Monitor backoff tick. Critically,
  // this is the only thing that revives a fully 'closed' cable after the
  // token-expired flow (the Monitor won't auto-recover from 'closed').
  private observeBrowserConnectivity(): void {
    const cable = (this.consumer as any).cable;

    const triggerReconnect = (reason: string) => {
      if (this._readyState !== "disconnected") return;
      console.debug(`[MicrositeEditor] ${reason}, triggering reconnect`);
      // reconnectNow() works from 'disconnected'/'pending_reconnect';
      // connect() works from 'closed' (after the token-expired flow).
      if (cable?.monitor?.reconnectNow?.()) return;
      cable?.connect?.()?.catch?.(() => {});
    };

    const handleOnline = () => triggerReconnect("browser went online");
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerReconnect("tab became visible while disconnected");
      }
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    this.browserConnectivityCleanup = () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }

  dispose(): void {
    if (this.syncCheckTimer) {
      clearTimeout(this.syncCheckTimer);
      this.syncCheckTimer = null;
    }
    this.syncedObserverCleanup?.();
    this.syncedObserverCleanup = null;
    this.browserConnectivityCleanup?.();
    this.browserConnectivityCleanup = null;
    this.provider.destroy();
    this.persistenceChannelSubscription?.unsubscribe();
    this.readyStateListeners.clear();
    this.saveErrorListeners.clear();
  }
}
